<script >
import { ColorType, createChart } from 'lightweight-charts';
import ChartingChartHeader from './ChartingChartHeader.vue';
import { formatNumberLabel } from '@/features/charting-next/adapters/chartingAdapterUtils';
import { normalizeChartingPriceSeriesType } from '@/features/charting-next/models/chartingPriceSeriesTypes';
import { CHARTING_PRICE_SCALE_ID, ChartingPriceLineLabels } from '@/features/charting-next/services/chartingPriceLineLabels';
import { CHARTING_OHLC_COLORS, ChartingOhlcSeries, toChartingOhlcData } from '@/features/charting-next/services/chartingOhlcSeries';
import { buildChartingMethodPoints, ChartingMethodPoints, methodPointPeriod, highlightedMethodPoints, toggleMethodPointSelection } from '@/features/charting-next/services/chartingMethodPoints';
import { useChartingLayout as useLayout } from '@/features/charting-next/services/chartingLayout';
import { onBeforeUnmount, onMounted, ref, watch } from '@vue/composition-api';
const __sfc_main = {};
__sfc_main.props = {
  candles: {
    type: Array,
    default: () => []
  },
  volumes: {
    type: Array,
    default: () => []
  },
  corporateActions: {
    type: Array,
    default: () => []
  },
  analysisOverlays: {
    type: Object,
    default: () => ({})
  },
  overlayVisibility: {
    type: Object,
    default: () => ({
      support: true,
      stop: true,
      resistance: true,
      trendLine: true,
      trendPoint: true,
      riskRewardPoint: true
    })
  },
  resolution: {
    type: String,
    default: 'M'
  },
  priceSeriesType: {
    type: String,
    default: 'bars'
  },
  showVolume: {
    type: Boolean,
    default: false
  },
  ticker: {
    type: String,
    default: ''
  },
  companyName: {
    type: String,
    default: ''
  },
  entryTypeLabel: {
    type: String,
    default: ''
  },
  showLegend: {
    type: Boolean,
    default: true
  },
  legendPlacement: {
    type: String,
    default: 'overlay'
  },
  legendExpanded: {
    type: Boolean,
    default: true
  },
  showLeftPriceScale: {
    type: Boolean,
    default: true
  },
  showRightPriceScale: {
    type: Boolean,
    default: true
  },
  showCurrentPriceLine: {
    type: Boolean,
    default: true
  },
  compact: {
    type: Boolean,
    default: false
  },
  interactive: {
    type: Boolean,
    default: true
  },
  seriesKey: {
    type: String,
    default: ''
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const props = __props;
  const emit = __ctx.emit;
  const containerRef = ref(null);
  const tooltipRef = ref(null);
  const corporateActionMarkerItems = ref([]);
  const methodPointItems = ref([]);
  const activeMethodPoints = ref([]);
  let methodPointsPrimitive;
  const selectedMethodPointIds = ref([]);
  let methodSelectionKey = '';
  let methodPointClickHandler;
  const chartLegend = ref({
    dateLabel: '',
    ohlcItems: [],
    overlayItems: []
  });
  const {
    isDarkTheme
  } = useLayout();
  let chart;
  let priceSeries;
  let volumeSeries;
  let trendLineSeries;
  let priceLineLabels;
  let trendPointCarrierSeries;
  let riskRewardPointCarrierSeries;
  let riskRewardPointPrimitive;
  let resizeObserver;
  let lastViewportSeriesKey = '';
  let activePriceLines = [];
  let timeScaleViewportHandler;
  let crosshairMoveHandler;
  let overlaySyncFrame = 0;
  let latestTrendLineData = [];
  const getChartAppearanceOptions = () => ({
    localization: {
      locale: 'es-ES',
      priceFormatter: price => formatLegendPrice(price)
    },
    layout: {
      background: {
        type: ColorType.Solid,
        color: isDarkTheme.value ? '#08111f' : '#ffffff'
      },
      textColor: isDarkTheme.value ? '#cbd5e1' : '#334155'
    },
    grid: {
      vertLines: {
        color: isDarkTheme.value ? 'rgba(148, 163, 184, 0.14)' : 'rgba(100, 116, 139, 0.2)'
      },
      horzLines: {
        color: isDarkTheme.value ? 'rgba(148, 163, 184, 0.14)' : 'rgba(100, 116, 139, 0.2)'
      }
    },
    // Left-hand badges are presentation only, never another price scale.
    leftPriceScale: {
      visible: false,
      borderVisible: false,
      minimumWidth: 0
    },
    rightPriceScale: {
      visible: !props.compact && props.showRightPriceScale,
      borderVisible: false,
      mode: 1
    },
    timeScale: {
      borderVisible: false,
      timeVisible: props.resolution === 'D'
    },
    crosshair: {
      mode: 0,
      vertLine: {
        color: isDarkTheme.value ? 'rgba(56, 189, 248, 0.48)' : 'rgba(14, 165, 233, 0.38)',
        width: 1,
        style: 2,
        visible: props.interactive
      },
      horzLine: {
        color: isDarkTheme.value ? 'rgba(56, 189, 248, 0.48)' : 'rgba(14, 165, 233, 0.38)',
        width: 1,
        style: 2,
        visible: props.interactive
      }
    },
    handleScroll: props.interactive,
    handleScale: props.interactive,
    kineticScroll: {
      mouse: props.interactive,
      touch: props.interactive
    }
  });
  const applyChartAppearance = () => {
    if (!chart) {
      return;
    }
    chart.applyOptions(getChartAppearanceOptions());
    volumeSeries?.applyOptions({
      visible: props.showVolume
    });
    trendLineSeries?.applyOptions(getTrendLineSeriesOptions());
    syncRiskRewardPoint();
    buildLegendState();
    syncPriceLineLabels();
    syncMethodPoints();
    scheduleOverlaySync();
  };
  const getSingleValueSeriesData = () => (props.candles || []).filter(item => item && item.time != null && typeof item.close === 'number').map(item => ({
    time: item.time,
    value: item.close
  }));
  const getPriceSeriesData = () => {
    const normalizedPriceSeriesType = normalizeChartingPriceSeriesType(props.priceSeriesType);
    if (normalizedPriceSeriesType === 'bars') {
      return toChartingOhlcData(props.candles || []);
    }
    return getSingleValueSeriesData();
  };
  const getPriceSeriesOptions = (priceSeriesType = props.priceSeriesType) => {
    const normalizedPriceSeriesType = normalizeChartingPriceSeriesType(priceSeriesType);
    const priceLineOptions = {
      priceScaleId: CHARTING_PRICE_SCALE_ID,
      priceLineVisible: props.showCurrentPriceLine,
      lastValueVisible: props.showCurrentPriceLine && props.showRightPriceScale
    };
    if (normalizedPriceSeriesType === 'area') {
      return {
        ...priceLineOptions,
        lineColor: isDarkTheme.value ? '#38bdf8' : '#0284c7',
        lineWidth: 2,
        topColor: isDarkTheme.value ? 'rgba(56, 189, 248, 0.28)' : 'rgba(14, 165, 233, 0.28)',
        bottomColor: isDarkTheme.value ? 'rgba(56, 189, 248, 0.04)' : 'rgba(14, 165, 233, 0.04)'
      };
    }
    return {
      ...priceLineOptions,
      ...CHARTING_OHLC_COLORS,
      openVisible: true
    };
  };
  const getTrendLineSeriesOptions = () => ({
    priceScaleId: CHARTING_PRICE_SCALE_ID,
    color: isDarkTheme.value ? '#38bdf8' : '#0284c7',
    lineWidth: 2,
    lineStyle: 0,
    lastValueVisible: false,
    priceLineVisible: false,
    crosshairMarkerVisible: false
  });
  const legendDateFormatter = new Intl.DateTimeFormat('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
  const isOverlayVisible = key => props.overlayVisibility?.[key] !== false;
  const isTrendLineVisible = () => isOverlayVisible('trendLine') && props.resolution === 'M' && props.priceSeriesType !== 'area';

  // Entry LT is now a point, not a configurable line layer. Its highlight is
  // controlled in Points; the old in-memory gear flag no longer hides it.
  const isTrendPointVisible = () => props.priceSeriesType !== 'area';
  const formatLegendPrice = value => {
    return formatNumberLabel(value, {
      maximumFractionDigits: 2
    }) || 'N/D';
  };
  const formatLegendDate = time => {
    const timestamp = normalizeChartTimestamp(time);
    if (timestamp === null) {
      return '';
    }
    return legendDateFormatter.format(new Date(timestamp * 1000));
  };
  class TrendPointPrimitiveRenderer {
    constructor(state) {
      this.state = state;
    }
    draw(target) {
      if (!this.state.visible) {
        return;
      }
      target.useMediaCoordinateSpace(({
        context
      }) => {
        context.save();
        try {
          context.beginPath();
          context.arc(this.state.x, this.state.y, this.state.radius, 0, Math.PI * 2);
          context.fillStyle = this.state.fillColor;
          context.strokeStyle = this.state.strokeColor;
          context.lineWidth = this.state.lineWidth;
          context.fill();
          context.stroke();
        } finally {
          context.restore();
        }
      });
    }
  }
  class TrendPointPrimitivePaneView {
    constructor(renderer) {
      this.rendererInstance = renderer;
    }
    zOrder() {
      return 'top';
    }
    renderer() {
      return this.rendererInstance;
    }
  }
  class TrendPointPrimitive {
    constructor() {
      this.point = null;
      this.chart = null;
      this.series = null;
      this.requestUpdate = null;
      this.state = {
        visible: false,
        x: 0,
        y: 0,
        radius: 6,
        lineWidth: 2,
        strokeColor: '#0284c7',
        fillColor: '#ffffff'
      };
      this.paneView = new TrendPointPrimitivePaneView(new TrendPointPrimitiveRenderer(this.state));
      this.paneViewItems = [this.paneView];
    }
    attached({
      chart,
      series,
      requestUpdate
    }) {
      this.chart = chart;
      this.series = series;
      this.requestUpdate = requestUpdate;
      this.recalculate();
    }
    detached() {
      this.chart = null;
      this.series = null;
      this.requestUpdate = null;
      this.state.visible = false;
    }
    paneViews() {
      return this.paneViewItems;
    }
    updateAllViews() {
      this.recalculate();
    }
    autoscaleInfo() {
      if (!this.point || !Number.isFinite(Number(this.point.price))) {
        return null;
      }
      return {
        priceRange: {
          minValue: Number(this.point.price),
          maxValue: Number(this.point.price)
        }
      };
    }
    setPoint(point) {
      this.point = point ? {
        ...point
      } : null;
      this.recalculate();
      this.requestUpdate?.();
    }
    recalculate() {
      this.state.visible = false;
      if (!this.chart || !this.series || !this.point) {
        return;
      }
      const x = this.chart.timeScale().timeToCoordinate(this.point.time);
      const y = this.series.priceToCoordinate(this.point.price);
      if (!Number.isFinite(Number(x)) || !Number.isFinite(Number(y))) {
        return;
      }
      this.state.visible = true;
      this.state.x = Number(x);
      this.state.y = Number(y);
      this.state.radius = Number(this.point.radius) || 6;
      this.state.lineWidth = Number(this.point.lineWidth) || 2;
      this.state.strokeColor = this.point.strokeColor || '#0284c7';
      this.state.fillColor = this.point.fillColor || '#ffffff';
    }
  }
  const clearPriceLines = () => {
    if (priceSeries && typeof priceSeries.removePriceLine === 'function') {
      activePriceLines.forEach(line => {
        try {
          priceSeries.removePriceLine(line);
        } catch (error) {
          return error;
        }
      });
    }
    activePriceLines = [];
  };
  const hideCorporateActionTooltip = () => {
    if (!tooltipRef.value) {
      return;
    }
    tooltipRef.value.style.display = 'none';
    tooltipRef.value.classList.remove('is-price-tooltip');
  };
  const hideRiskRewardPoint = () => {
    riskRewardPointPrimitive?.setPoint(null);
  };
  const showCorporateActionTooltip = ({
    point,
    action
  }) => {
    if (!tooltipRef.value || !containerRef.value || !point || !action) {
      hideCorporateActionTooltip();
      return;
    }
    const tooltipLines = Array.isArray(action.tooltip) ? action.tooltip.filter(Boolean) : [];
    if (!tooltipLines.length) {
      hideCorporateActionTooltip();
      return;
    }
    tooltipRef.value.classList.remove('is-price-tooltip');
    tooltipRef.value.replaceChildren(...tooltipLines.map(line => {
      const row = document.createElement('div');
      row.textContent = line;
      return row;
    }));
    tooltipRef.value.style.display = 'block';
    const containerWidth = containerRef.value.clientWidth || 0;
    const tooltipWidth = tooltipRef.value.offsetWidth || 0;
    const x = Math.min(Math.max(point.x + 12, 8), Math.max(8, containerWidth - tooltipWidth - 8));
    const y = Math.max(point.y - 56, 8);
    tooltipRef.value.style.left = `${x}px`;
    tooltipRef.value.style.top = `${y}px`;
    tooltipRef.value.style.borderColor = action.color || '#0088cc';
  };
  const resolveCrosshairPrice = event => {
    const seriesData = event?.seriesData && priceSeries ? event.seriesData.get(priceSeries) : null;
    if (Number.isFinite(Number(seriesData?.value))) {
      return Number(seriesData.value);
    }
    if (Number.isFinite(Number(seriesData?.close))) {
      return Number(seriesData.close);
    }
    const candle = findLegendCandle(event?.time);
    return Number.isFinite(Number(candle?.close)) ? Number(candle.close) : null;
  };
  const showCompactPriceTooltip = (event, insideChart) => {
    if (!props.compact) {
      return;
    }
    if (!tooltipRef.value || !containerRef.value || !insideChart || event?.time === undefined) {
      hideCorporateActionTooltip();
      return;
    }
    const price = resolveCrosshairPrice(event);
    if (!Number.isFinite(Number(price))) {
      hideCorporateActionTooltip();
      return;
    }
    const dateLabel = formatLegendDate(event.time);
    const value = document.createElement('div');
    value.className = 'charting-lite-chart-tooltip__value';
    value.textContent = formatLegendPrice(price);
    if (dateLabel) {
      const date = document.createElement('div');
      date.className = 'charting-lite-chart-tooltip__date';
      date.textContent = dateLabel;
      tooltipRef.value.replaceChildren(value, date);
    } else {
      tooltipRef.value.replaceChildren(value);
    }
    tooltipRef.value.classList.add('is-price-tooltip');
    tooltipRef.value.style.display = 'block';
    tooltipRef.value.style.borderColor = isDarkTheme.value ? 'rgba(56, 189, 248, 0.42)' : 'rgba(14, 165, 233, 0.32)';
    const containerWidth = containerRef.value.clientWidth || 0;
    const containerHeight = containerRef.value.clientHeight || 0;
    const tooltipWidth = tooltipRef.value.offsetWidth || 0;
    const tooltipHeight = tooltipRef.value.offsetHeight || 0;
    const priceY = priceSeries?.priceToCoordinate(price);
    const tooltipSourceY = Number.isFinite(Number(priceY)) ? Number(priceY) : event.point.y;
    const tooltipPadding = 8;
    const preferredTooltipY = tooltipSourceY - tooltipHeight - 10;
    const maxTooltipY = Math.max(tooltipPadding, containerHeight - tooltipHeight - tooltipPadding);
    const x = Math.min(Math.max(event.point.x + 10, 8), Math.max(8, containerWidth - tooltipWidth - 8));
    const y = Math.min(Math.max(preferredTooltipY, tooltipPadding), maxTooltipY);
    tooltipRef.value.style.left = `${x}px`;
    tooltipRef.value.style.top = `${y}px`;
  };
  const syncCorporateActionMarkers = () => {
    if (!chart || !containerRef.value) {
      corporateActionMarkerItems.value = [];
      return;
    }
    const containerWidth = containerRef.value.clientWidth || 0;
    const firstCandleTime = props.candles?.[0]?.time ?? null;
    const lastCandleTime = (props.candles || []).at(-1)?.time ?? null;
    corporateActionMarkerItems.value = (props.corporateActions || []).map(action => {
      if (Number.isFinite(Number(firstCandleTime)) && Number.isFinite(Number(lastCandleTime)) && (Number(action?.time) < Number(firstCandleTime) || Number(action?.time) > Number(lastCandleTime))) {
        return null;
      }
      const anchorTime = findNearestCandleTime(action?.time);
      const x = chart.timeScale().timeToCoordinate(anchorTime ?? action?.time);
      if (x === null || !Number.isFinite(x)) {
        return null;
      }

      // Off-screen events leave the viewport with their bars, rather than
      // sticking to its edges or mistaking an unavailable coordinate for 0.
      const plotWidth = chart.timeScale().width();
      if (x < 0 || x > Math.min(plotWidth, containerWidth)) return null;
      return {
        id: String(action?.id || action?.marker?.id || ''),
        x,
        time: anchorTime ?? action?.time,
        label: String(action?.label || ''),
        color: action?.color || action?.marker?.color || '#0088cc',
        tooltip: Array.isArray(action?.tooltip) ? action.tooltip.filter(Boolean) : []
      };
    }).filter(Boolean);
  };
  const handleCorporateActionHover = (action, event) => {
    if (!containerRef.value || !action) {
      hideCorporateActionTooltip();
      return;
    }
    const rect = containerRef.value.getBoundingClientRect();
    showCorporateActionTooltip({
      point: {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      },
      action
    });
  };
  const getOverlayPointPayload = (point, visible = true) => {
    const latestCandle = (props.candles || []).at(-1);
    if (!visible || !point || !latestCandle?.time || !Number.isFinite(Number(point.price))) {
      return null;
    }
    return {
      time: latestCandle.time,
      price: Number(point.price),
      radius: 6,
      lineWidth: 2,
      strokeColor: point.color || '#0284c7',
      fillColor: isDarkTheme.value ? '#08111f' : '#ffffff'
    };
  };
  const getRiskRewardPointPayload = () => getOverlayPointPayload(props.analysisOverlays?.riskRewardPoint, isOverlayVisible('riskRewardPoint'));
  const syncRiskRewardPoint = () => {
    if (!riskRewardPointPrimitive) {
      return;
    }
    riskRewardPointPrimitive.setPoint(getRiskRewardPointPayload());
  };
  const createPriceSeries = () => {
    if (!chart) {
      return;
    }
    if (priceSeries) {
      clearPriceLines();
      if (priceLineLabels) priceSeries.detachPrimitive(priceLineLabels);
      if (methodPointsPrimitive) priceSeries.detachPrimitive(methodPointsPrimitive);
      chart.removeSeries(priceSeries);
      priceSeries = undefined;
    }
    const normalizedPriceSeriesType = normalizeChartingPriceSeriesType(props.priceSeriesType);
    if (normalizedPriceSeriesType === 'area') {
      priceSeries = chart.addAreaSeries(getPriceSeriesOptions(normalizedPriceSeriesType));
    } else {
      priceSeries = chart.addCustomSeries(new ChartingOhlcSeries(), getPriceSeriesOptions(normalizedPriceSeriesType));
    }
    priceLineLabels = new ChartingPriceLineLabels(formatLegendPrice);
    priceSeries.attachPrimitive(priceLineLabels);
    methodPointsPrimitive = new ChartingMethodPoints();
    priceSeries.attachPrimitive(methodPointsPrimitive);
  };
  const createTrendPointCarrierSeries = () => {
    if (!chart) {
      return;
    }
    if (trendPointCarrierSeries) {
      chart.removeSeries(trendPointCarrierSeries);
      trendPointCarrierSeries = undefined;
    }
    trendPointCarrierSeries = chart.addLineSeries({
      priceScaleId: CHARTING_PRICE_SCALE_ID,
      color: 'rgba(0, 0, 0, 0)',
      lineWidth: 1,
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false
    });

    // Keep the invisible carrier for the existing native autoscale behaviour;
    // the circle itself is drawn only once by ChartingMethodPoints.
  };

  const createRiskRewardPointCarrierSeries = () => {
    if (!chart) {
      return;
    }
    if (riskRewardPointCarrierSeries) {
      if (riskRewardPointPrimitive && typeof riskRewardPointCarrierSeries.detachPrimitive === 'function') {
        riskRewardPointCarrierSeries.detachPrimitive(riskRewardPointPrimitive);
      }
      chart.removeSeries(riskRewardPointCarrierSeries);
      riskRewardPointCarrierSeries = undefined;
      riskRewardPointPrimitive = undefined;
    }
    riskRewardPointCarrierSeries = chart.addLineSeries({
      priceScaleId: CHARTING_PRICE_SCALE_ID,
      color: 'rgba(0, 0, 0, 0)',
      lineWidth: 1,
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false
    });
    if (typeof riskRewardPointCarrierSeries.attachPrimitive === 'function') {
      riskRewardPointPrimitive = new TrendPointPrimitive();
      riskRewardPointCarrierSeries.attachPrimitive(riskRewardPointPrimitive);
    }
  };
  const buildPriceLineOptions = line => ({
    price: Number(line.price),
    color: line.color || '#0284c7',
    lineWidth: 2,
    lineStyle: Number.isFinite(Number(line.lineStyle)) ? Number(line.lineStyle) : 0,
    axisLabelVisible: true,
    axisLabelColor: line.color || '#0284c7',
    axisLabelTextColor: '#f8fafc',
    title: line.label || ''
  });
  const buildAnalysisPriceLines = () => Object.entries(props.analysisOverlays?.priceLines || {}).filter(([key, line]) => {
    if (!line || !Number.isFinite(Number(line.price))) {
      return false;
    }
    return isOverlayVisible(key);
  }).map(([, line]) => ({
    ...buildPriceLineOptions(line)
  }));
  const normalizeChartTimestamp = value => {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      if (value > 10000000000) {
        return Math.floor(value / 1000);
      }
      if (value > 100000000) {
        return Math.floor(value);
      }
      return null;
    }
    const parsed = Date.parse(String(value));
    return Number.isFinite(parsed) ? Math.floor(parsed / 1000) : null;
  };
  const findNearestCandleIndexByTime = timestamp => {
    const candles = props.candles || [];
    if (!candles.length || !Number.isFinite(Number(timestamp))) {
      return null;
    }
    let nearestIndex = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;
    candles.forEach((candle, index) => {
      const candleTime = Number(candle?.time);
      if (!Number.isFinite(candleTime)) {
        return;
      }
      const distance = Math.abs(candleTime - Number(timestamp));
      if (distance < nearestDistance) {
        nearestIndex = index;
        nearestDistance = distance;
      }
    });
    return nearestIndex;
  };
  const findNearestCandleTime = timestamp => {
    const nearestIndex = findNearestCandleIndexByTime(timestamp);
    return nearestIndex === null ? null : props.candles?.[nearestIndex]?.time ?? null;
  };
  const findLegendCandle = time => {
    const candles = props.candles || [];
    if (!candles.length) {
      return null;
    }
    const normalizedTime = normalizeChartTimestamp(time);
    if (normalizedTime === null) {
      return candles.at(-1) || null;
    }
    const nearestIndex = findNearestCandleIndexByTime(normalizedTime);
    return nearestIndex === null ? candles.at(-1) || null : candles[nearestIndex] || candles.at(-1) || null;
  };
  const getTrendLineValueAtTime = time => {
    if (!isTrendLineVisible() || !latestTrendLineData.length) {
      return null;
    }
    const normalizedTime = normalizeChartTimestamp(time);
    if (normalizedTime === null) {
      return null;
    }
    const matchingPoint = latestTrendLineData.find(point => Number(point?.time) === normalizedTime && Number.isFinite(Number(point?.value)));
    return matchingPoint ? Number(matchingPoint.value) : null;
  };
  const buildLegendState = (time = null) => {
    const candle = findLegendCandle(time);
    if (!candle) {
      chartLegend.value = {
        dateLabel: '',
        ohlcItems: [],
        overlayItems: []
      };
      return;
    }
    const overlayItems = [];
    const priceLines = props.analysisOverlays?.priceLines || {};
    const trendPoint = props.analysisOverlays?.trendPoint;
    const riskRewardPoint = props.analysisOverlays?.riskRewardPoint;
    const trendLineValue = getTrendLineValueAtTime(candle.time);
    if (isOverlayVisible('support') && Number.isFinite(Number(priceLines.support?.price))) {
      overlayItems.push({
        key: 'support',
        label: priceLines.support.label || 'Soporte',
        value: formatLegendPrice(priceLines.support.price),
        color: priceLines.support.color || '#dc2626'
      });
    }
    if (isOverlayVisible('stop') && Number.isFinite(Number(priceLines.stop?.price))) {
      overlayItems.push({
        key: 'stop',
        label: priceLines.stop.label || 'Stop',
        value: formatLegendPrice(priceLines.stop.price),
        color: priceLines.stop.color || '#dc2626'
      });
    }
    if (isOverlayVisible('resistance') && Number.isFinite(Number(priceLines.resistance?.price))) {
      overlayItems.push({
        key: 'resistance',
        label: priceLines.resistance.label || 'Resistencia',
        value: formatLegendPrice(priceLines.resistance.price),
        color: priceLines.resistance.color || '#16a34a'
      });
    }
    if (isTrendLineVisible() && Number.isFinite(Number(trendLineValue))) {
      overlayItems.push({
        key: 'trendLine',
        label: 'LT',
        value: formatLegendPrice(trendLineValue),
        color: getTrendLineSeriesOptions().color
      });
    }
    if (isTrendPointVisible() && Number.isFinite(Number(trendPoint?.price))) {
      overlayItems.push({
        key: 'trendPoint',
        label: trendPoint.label || 'Punto LT',
        value: formatLegendPrice(trendPoint.price),
        color: trendPoint.color || '#0284c7'
      });
    }
    if (isOverlayVisible('riskRewardPoint') && Number.isFinite(Number(riskRewardPoint?.price))) {
      overlayItems.push({
        key: 'riskRewardPoint',
        label: riskRewardPoint.label || 'Punto RR',
        value: formatLegendPrice(riskRewardPoint.price),
        color: riskRewardPoint.color || '#0284c7'
      });
    }
    chartLegend.value = {
      dateLabel: formatLegendDate(candle.time),
      ohlcItems: [{
        key: 'open',
        label: 'O',
        value: formatLegendPrice(candle.open)
      }, {
        key: 'high',
        label: 'H',
        value: formatLegendPrice(candle.high)
      }, {
        key: 'low',
        label: 'L',
        value: formatLegendPrice(candle.low)
      }, {
        key: 'close',
        label: 'C',
        value: formatLegendPrice(candle.close)
      }],
      overlayItems
    };
  };
  const scheduleOverlaySync = () => {
    if (typeof window === 'undefined') {
      syncCorporateActionMarkers();
      return;
    }

    // Coalesce events within a frame, never postpone a pending update. Cancelling
    // a nested RAF on every range change starves markers throughout a drag.
    if (overlaySyncFrame) return;
    overlaySyncFrame = window.requestAnimationFrame(() => {
      overlaySyncFrame = 0;
      syncCorporateActionMarkers();
    });
  };
  const resolveTrendAnchorIndex = (dateValue, fallbackIndex) => {
    const numericIndex = Number(fallbackIndex);
    if (fallbackIndex !== null && fallbackIndex !== undefined && Number.isFinite(numericIndex) && props.candles?.length) {
      return Number.isInteger(numericIndex) && numericIndex >= 0 && numericIndex < props.candles.length ? numericIndex : null;
    }
    const period = methodPointPeriod(dateValue, props.resolution);
    if (period !== null) {
      const index = (props.candles || []).findIndex(candle => methodPointPeriod(candle.time, props.resolution) === period);
      return index < 0 ? null : index;
    }
    return null;
  };
  const buildTrendLineData = () => {
    const candles = props.candles || [];
    const trendLine = props.analysisOverlays?.trendLine;
    if (!candles.length || !trendLine || !isTrendLineVisible()) {
      return [];
    }
    let startIndex = resolveTrendAnchorIndex(trendLine.startDate, trendLine.startIndex);
    let endIndex = resolveTrendAnchorIndex(trendLine.endDate, trendLine.endIndex);
    let startPrice = Number(trendLine.startPrice);
    let endPrice = Number(trendLine.endPrice);
    if (startIndex === null || endIndex === null || startIndex === endIndex || !Number.isFinite(startPrice) || !Number.isFinite(endPrice) || startPrice <= 0 || endPrice <= 0) {
      return [];
    }
    if (startIndex > endIndex) {
      [startIndex, endIndex] = [endIndex, startIndex];
      [startPrice, endPrice] = [endPrice, startPrice];
    }
    const bullish = Number(trendLine.direction) !== 0;
    const startCandle = candles[startIndex];
    const endCandle = candles[endIndex];
    const startAnchorPrice = Number(bullish ? startCandle?.low : startCandle?.high);
    const endAnchorPrice = Number(bullish ? endCandle?.low : endCandle?.high);
    const normalizedStartPrice = Number.isFinite(startAnchorPrice) && startAnchorPrice > 0 ? startAnchorPrice : startPrice;
    const normalizedEndPrice = Number.isFinite(endAnchorPrice) && endAnchorPrice > 0 ? endAnchorPrice : endPrice;
    const slope = Math.log(normalizedEndPrice / normalizedStartPrice) / (endIndex - startIndex);
    if (!Number.isFinite(slope)) {
      return [];
    }
    return candles.slice(startIndex).map((candle, offset) => {
      const index = startIndex + offset;
      const projectedPrice = Math.exp(Math.log(normalizedStartPrice) + slope * (index - startIndex));
      if (!Number.isFinite(projectedPrice) || projectedPrice < 0.1) {
        return {
          time: candle.time
        };
      }
      if (index > endIndex) {
        const trendBroken = bullish ? Number(candle.low) < projectedPrice : Number(candle.high) > projectedPrice;
        if (trendBroken) {
          return {
            time: candle.time
          };
        }
      }
      return {
        time: candle.time,
        value: projectedPrice
      };
    });
  };
  const syncPriceLineLabels = () => {
    priceLineLabels?.setLines(buildAnalysisPriceLines(), {
      showPrices: props.showLeftPriceScale && !props.compact
    });
  };
  const syncPriceLines = () => {
    clearPriceLines();
    if (!priceSeries) return;
    buildAnalysisPriceLines().forEach(line => {
      activePriceLines.push(priceSeries.createPriceLine({
        ...line,
        axisLabelVisible: false,
        title: ''
      }));
    });
    syncPriceLineLabels();
  };
  const syncTrendLineSeries = () => {
    if (!chart) {
      return;
    }
    const trendLineData = buildTrendLineData();
    latestTrendLineData = trendLineData;
    if (!trendLineData.length) {
      if (trendLineSeries) {
        chart.removeSeries(trendLineSeries);
        trendLineSeries = undefined;
      }
      return;
    }
    if (!trendLineSeries) {
      trendLineSeries = chart.addLineSeries(getTrendLineSeriesOptions());
    }
    trendLineSeries.applyOptions(getTrendLineSeriesOptions());
    trendLineSeries.setData(trendLineData);
  };
  const activateMethodPoint = (id, pin = false) => {
    const selected = methodPointItems.value.find(point => point.id === id);
    if (pin && selected) selectedMethodPointIds.value = toggleMethodPointSelection(selectedMethodPointIds.value, selected.id);
    activeMethodPoints.value = highlightedMethodPoints(methodPointItems.value, selectedMethodPointIds.value, pin ? null : selected?.id);
    methodPointsPrimitive?.setActive(activeMethodPoints.value.map(point => point.id));
  };
  const clearMethodPoint = () => {
    selectedMethodPointIds.value = [];
    activateMethodPoint(null);
  };
  const syncMethodPoints = () => {
    if (!methodPointsPrimitive) return;
    const trend = props.analysisOverlays?.trendLine;
    const trendAnchors = [];
    if (trend && isTrendLineVisible() && latestTrendLineData.length) {
      for (const [date, fallback] of [[trend.startDate, trend.startIndex], [trend.endDate, trend.endIndex]]) {
        trendAnchors.push(null);
        // Use the very same anchor/price as the displayed LT, not a second
        // reconstruction from rounded prices. Ignore absent/out-of-range indices.
        if (!date && (fallback === null || fallback === undefined)) continue;
        if (date && !props.candles.some(candle => methodPointPeriod(candle.time, props.resolution) === methodPointPeriod(date, props.resolution))) continue;
        if (fallback != null && (!Number.isInteger(Number(fallback)) || Number(fallback) < 0 || Number(fallback) >= props.candles.length)) continue;
        const index = resolveTrendAnchorIndex(date, fallback);
        const candle = index === null ? null : props.candles[index];
        const point = candle && latestTrendLineData.find(item => item.time === candle.time);
        if (point && Number.isFinite(point.value)) trendAnchors[trendAnchors.length - 1] = {
          time: point.time,
          price: point.value,
          date: date || point.time
        };
      }
    }
    methodPointItems.value = props.priceSeriesType === 'area' ? [] : buildChartingMethodPoints({
      candles: props.candles,
      resolution: props.resolution,
      support: props.analysisOverlays?.priceLines?.support,
      supportVisible: isOverlayVisible('support'),
      trendAnchors,
      entryPoint: props.analysisOverlays?.trendPoint
    });
    const nextKey = `${props.seriesKey}|${props.ticker}|${props.resolution}|${props.priceSeriesType}`;
    selectedMethodPointIds.value = methodSelectionKey === nextKey ? selectedMethodPointIds.value.filter(id => methodPointItems.value.some(point => point.id === id)) : [];
    methodSelectionKey = nextKey;
    methodPointsPrimitive.setPoints(methodPointItems.value, {
      dark: isDarkTheme.value,
      interactive: props.interactive
    });
    activateMethodPoint(null);
  };
  const resizeChart = () => {
    if (!chart || !containerRef.value) {
      return;
    }
    const rect = containerRef.value.getBoundingClientRect();
    if (!rect.width || !rect.height) {
      return;
    }
    chart.applyOptions({
      width: rect.width,
      height: rect.height
    });
    scheduleOverlaySync();
  };
  const syncSeries = () => {
    if (!priceSeries || !volumeSeries) {
      return;
    }
    const trendPoint = props.analysisOverlays?.trendPoint;
    const riskRewardPoint = props.analysisOverlays?.riskRewardPoint;
    const latestCandle = (props.candles || []).at(-1);
    priceSeries.applyOptions(getPriceSeriesOptions());
    priceSeries.setData(getPriceSeriesData());
    priceSeries.setMarkers([]);
    syncPriceLines();
    volumeSeries.setData(props.volumes || []);
    volumeSeries.setMarkers?.([]);
    trendPointCarrierSeries?.setData(isTrendPointVisible() && trendPoint && latestCandle?.time && Number.isFinite(Number(trendPoint.price)) ? [{
      time: latestCandle.time,
      value: Number(trendPoint.price)
    }] : []);
    riskRewardPointCarrierSeries?.setData(isOverlayVisible('riskRewardPoint') && riskRewardPoint && latestCandle?.time && Number.isFinite(Number(riskRewardPoint.price)) ? [{
      time: latestCandle.time,
      value: Number(riskRewardPoint.price)
    }] : []);
    syncTrendLineSeries();
    syncMethodPoints();
    const normalizedSeriesKey = String(props.seriesKey || '').trim();
    if ((props.candles || []).length && normalizedSeriesKey !== lastViewportSeriesKey) {
      lastViewportSeriesKey = normalizedSeriesKey;
      chart.timeScale().fitContent();
    }
    syncRiskRewardPoint();
    buildLegendState();
    scheduleOverlaySync();
  };
  const mountChart = () => {
    if (!containerRef.value || chart) {
      return;
    }
    const rect = containerRef.value.getBoundingClientRect();
    chart = createChart(containerRef.value, {
      width: rect.width || 640,
      height: rect.height || 440,
      ...getChartAppearanceOptions()
    });
    createPriceSeries();
    createTrendPointCarrierSeries();
    createRiskRewardPointCarrierSeries();
    volumeSeries = chart.addHistogramSeries({
      priceFormat: {
        type: 'volume'
      },
      priceScaleId: '',
      visible: props.showVolume,
      scaleMargins: {
        top: 0.82,
        bottom: 0
      }
    });
    syncSeries();
    timeScaleViewportHandler = () => {
      hideCorporateActionTooltip();
      activateMethodPoint(null);
      scheduleOverlaySync();
    };
    crosshairMoveHandler = event => {
      const point = event?.point;
      const insideChart = point && containerRef.value && Number.isFinite(Number(point.x)) && Number.isFinite(Number(point.y)) && point.x >= 0 && point.y >= 0 && point.x <= containerRef.value.clientWidth && point.y <= containerRef.value.clientHeight;
      buildLegendState(insideChart && event?.time !== undefined ? event.time : null);
      // In LC 4.2 crosshairMove precedes the native hovered-object update.
      // Hit-test these same rendered coordinates directly (also works on tap).
      activateMethodPoint(insideChart ? methodPointsPrimitive?.hitTest(point.x, point.y)?.externalId : null);
      showCompactPriceTooltip(event, insideChart);
    };
    if (props.interactive) {
      methodPointClickHandler = event => {
        const point = event?.point;
        const id = point ? methodPointsPrimitive?.hitTest(point.x, point.y)?.externalId : null;
        if (methodPointItems.value.some(point => point.id === id)) activateMethodPoint(id, true);else clearMethodPoint();
      };
      chart.subscribeClick(methodPointClickHandler);
      chart.subscribeCrosshairMove(crosshairMoveHandler);
      chart.timeScale().subscribeVisibleLogicalRangeChange(timeScaleViewportHandler);
      chart.timeScale().subscribeSizeChange(timeScaleViewportHandler);
    }
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => resizeChart());
      resizeObserver.observe(containerRef.value);
    } else {
      window.addEventListener('resize', resizeChart);
    }
  };
  watch(() => props.resolution, () => {
    applyChartAppearance();
    syncSeries();
  });
  watch(isDarkTheme, () => {
    applyChartAppearance();
  });
  watch(() => props.priceSeriesType, () => {
    if (!chart) {
      return;
    }
    createPriceSeries();
    createTrendPointCarrierSeries();
    createRiskRewardPointCarrierSeries();
    syncSeries();
  });
  watch(() => props.showVolume, () => {
    if (!volumeSeries) {
      return;
    }
    volumeSeries.applyOptions({
      visible: props.showVolume
    });
    scheduleOverlaySync();
  });
  watch(() => [props.showLeftPriceScale, props.showRightPriceScale, props.showCurrentPriceLine], () => {
    applyChartAppearance();
    priceSeries?.applyOptions(getPriceSeriesOptions());
  });
  watch(() => [props.candles, props.volumes, props.corporateActions, props.analysisOverlays, props.overlayVisibility, props.seriesKey], () => {
    syncSeries();
  }, {
    deep: true
  });
  onMounted(() => {
    mountChart();
  });
  onBeforeUnmount(() => {
    resizeObserver?.disconnect();
    window.removeEventListener('resize', resizeChart);
    if (overlaySyncFrame) {
      window.cancelAnimationFrame(overlaySyncFrame);
      overlaySyncFrame = 0;
    }
    if (chart && timeScaleViewportHandler) {
      chart.timeScale().unsubscribeVisibleLogicalRangeChange(timeScaleViewportHandler);
      chart.timeScale().unsubscribeSizeChange(timeScaleViewportHandler);
    }
    if (chart && crosshairMoveHandler) {
      chart.unsubscribeCrosshairMove(crosshairMoveHandler);
    }
    if (chart && methodPointClickHandler) chart.unsubscribeClick(methodPointClickHandler);
    if (priceSeries && methodPointsPrimitive) priceSeries.detachPrimitive(methodPointsPrimitive);
    methodPointsPrimitive = undefined;
    methodPointClickHandler = undefined;
    methodPointItems.value = [];
    activeMethodPoints.value = [];
    hideCorporateActionTooltip();
    hideRiskRewardPoint();
    if (priceLineLabels && priceSeries) priceSeries.detachPrimitive(priceLineLabels);
    if (riskRewardPointCarrierSeries && riskRewardPointPrimitive && typeof riskRewardPointCarrierSeries.detachPrimitive === 'function') {
      riskRewardPointCarrierSeries.detachPrimitive(riskRewardPointPrimitive);
    }
    chart?.remove();
    chart = undefined;
    priceSeries = undefined;
    volumeSeries = undefined;
    trendLineSeries = undefined;
    priceLineLabels = undefined;
    trendPointCarrierSeries = undefined;
    riskRewardPointCarrierSeries = undefined;
    riskRewardPointPrimitive = undefined;
    activePriceLines = [];
    corporateActionMarkerItems.value = [];
    timeScaleViewportHandler = undefined;
    crosshairMoveHandler = undefined;
    latestTrendLineData = [];
  });
  return {
    emit,
    containerRef,
    tooltipRef,
    corporateActionMarkerItems,
    methodPointItems,
    activeMethodPoints,
    selectedMethodPointIds,
    chartLegend,
    formatLegendPrice,
    formatLegendDate,
    hideCorporateActionTooltip,
    handleCorporateActionHover,
    activateMethodPoint,
    clearMethodPoint
  };
};
__sfc_main.components = Object.assign({
  ChartingChartHeader
}, __sfc_main.components);
export default __sfc_main;
</script>

<template>
  <div class="charting-lite-chart-frame">
    <ChartingChartHeader
        v-if="showLegend && legendPlacement === 'external'"
        :ticker="ticker" :company-name="companyName" :entry-type-label="entryTypeLabel"
        :legend="chartLegend" :expanded="legendExpanded"
        @update:expanded="emit('update:legend-expanded', $event)"
    />
    <div
        ref="containerRef"
        class="charting-lite-chart"
        @keydown.esc.stop="clearMethodPoint"
        :class="{
            'is-compact': compact,
            'is-non-interactive': !interactive,
            'is-left-scale-hidden': !showLeftPriceScale,
            'is-right-scale-hidden': !showRightPriceScale
        }"
    >
        <div v-if="showLegend && legendPlacement === 'overlay' && (chartLegend.ohlcItems.length || chartLegend.overlayItems.length)" class="charting-lite-chart-legend" aria-hidden="true">
            <div v-if="ticker || companyName" class="charting-lite-chart-legend-heading">
                <span v-if="ticker" class="charting-lite-chart-legend-ticker">{{ ticker }}</span>
                <span v-if="entryTypeLabel" class="charting-lite-chart-legend-entry">{{ entryTypeLabel }}</span>
                <span v-if="companyName" class="charting-lite-chart-legend-name">{{ companyName }}</span>
            </div>
            <div class="charting-lite-chart-legend-primary">
                <span v-if="chartLegend.dateLabel" class="charting-lite-chart-legend-date">{{ chartLegend.dateLabel }}</span>
                <span v-for="item in chartLegend.ohlcItems" :key="item.key" class="charting-lite-chart-legend-token">
                    <span class="charting-lite-chart-legend-token-label">{{ item.label }}</span>
                    <span class="charting-lite-chart-legend-token-value">{{ item.value }}</span>
                </span>
            </div>
            <div v-if="chartLegend.overlayItems.length" class="charting-lite-chart-legend-secondary">
                <span
                    v-for="item in chartLegend.overlayItems"
                    :key="item.key"
                    class="charting-lite-chart-legend-token"
                    :style="{ '--charting-legend-accent': item.color }"
                >
                    <span class="charting-lite-chart-legend-token-label charting-lite-chart-legend-token-label--accent">{{ item.label }}</span>
                    <span class="charting-lite-chart-legend-token-value">{{ item.value }}</span>
                </span>
            </div>
        </div>
        <div ref="tooltipRef" class="charting-lite-chart-tooltip"></div>
        <div v-if="interactive && methodPointItems.length" class="charting-method-points" @click.stop @pointerdown.stop @touchstart.stop>
            <div v-if="activeMethodPoints.length" class="charting-method-points__detail" role="status" aria-live="polite">
                <div v-for="point in activeMethodPoints" :key="point.id" class="charting-method-points__entry">
                    <strong>{{ point.label }}</strong>
                    <span>{{ point.date ? formatLegendDate(point.date) : 'Nivel actual' }} · {{ formatLegendPrice(point.price) }}</span>
                    <span v-if="point.description">{{ point.description }}</span>
                </div>
                <button type="button" class="charting-method-points__close" aria-label="Cerrar detalle del punto" @click="clearMethodPoint">×</button>
            </div>
            <div class="charting-method-points__controls" role="group" aria-label="Puntos del método">
                <span class="charting-method-points__caption">Puntos</span>
                <button
                    v-for="point in methodPointItems" :key="point.id" type="button"
                    :title="point.label" :aria-label="point.label"
                    :aria-pressed="selectedMethodPointIds.includes(point.id) ? 'true' : 'false'"
                    :style="{ '--method-point-color': point.color }"
                    @click="activateMethodPoint(point.id, true)"
                ><span aria-hidden="true">{{ point.shape === 'circle' ? '○' : '◇' }}</span> {{ point.shortLabel }}</button>
            </div>
        </div>
        <div class="charting-lite-chart-corporate-actions-layer" aria-hidden="true">
            <div
                v-for="action in corporateActionMarkerItems"
                :key="action.id"
                class="charting-lite-chart-corporate-action"
                :style="{
                    left: `${action.x}px`,
                    '--charting-corporate-action-color': action.color
                }"
                @mouseenter="handleCorporateActionHover(action, $event)"
                @mousemove="handleCorporateActionHover(action, $event)"
                @mouseleave="hideCorporateActionTooltip"
            >
                {{ action.label }}
            </div>
        </div>
    </div>
  </div>
</template>

<style scoped>
.charting-method-points { position: absolute; z-index: 4; left: 12px; bottom: 58px; max-width: calc(100% - 40px); max-height: calc(100% - 74px); display: flex; flex-direction: column; align-items: flex-start; color: var(--text-color, #334155); }
.charting-method-points__controls { display: flex; flex-wrap: wrap; flex-shrink: 0; align-items: center; gap: 4px; width: fit-content; max-width: 100%; box-sizing: border-box; padding: 3px 5px; border: 1px solid var(--surface-border, #dbe2ea); border-radius: 10px; background: var(--surface-card, #fff); box-shadow: 0 2px 8px #00000008; }
.charting-method-points__caption { font-size: 10px; padding: 0 4px; color: var(--text-color-secondary, #64748b); }
.charting-method-points__controls button { border: 0; border-radius: 6px; background: transparent; color: inherit; font: inherit; font-size: 11px; font-weight: 700; min-height: 28px; padding: 2px 6px; cursor: pointer; }
.charting-method-points__controls button span { color: var(--method-point-color); font-size: 15px; }
.charting-method-points__controls button[aria-pressed="true"] { background: color-mix(in srgb, var(--method-point-color) 16%, transparent); }
.charting-method-points button:focus-visible { outline: 2px solid #0284c7; outline-offset: 2px; }
.charting-method-points__detail { position: relative; min-height: 0; max-width: 260px; box-sizing: border-box; overflow-y: auto; margin-bottom: 8px; padding: 12px 32px 12px 12px; border: 1px solid var(--surface-border, #dbe2ea); border-radius: 12px; background: var(--surface-card, #fff); box-shadow: 0 3px 14px #00000010; }
.charting-method-points__entry { display: flex; flex-direction: column; gap: 3px; font-size: 12px; }
.charting-method-points__entry + .charting-method-points__entry { margin-top: 9px; }
.charting-method-points__entry span { color: var(--text-color-secondary, #64748b); font-size: 11px; }
.charting-method-points__close { position: absolute; right: 3px; top: 3px; width: 28px; height: 28px; border: 0; background: transparent; color: inherit; font-size: 20px; cursor: pointer; }
@media (pointer: coarse), (max-width: 767px) {
    .charting-method-points__controls button { min-width: 40px; min-height: 40px; }
    .charting-method-points__close { width: 40px; height: 40px; }
    .charting-method-points__detail { padding-right: 44px; }
}
.charting-lite-chart-frame { display: flex; flex-direction: column; width: 100%; height: 100%; min-width: 0; min-height: 0; }
.charting-lite-chart-frame > .charting-lite-chart { flex: 1 1 0; height: auto; }
.charting-lite-chart {
    position: relative;
    width: 100%;
    height: 100%;
    min-height: 0;
}

.charting-lite-chart.is-compact {
    min-height: 0;
}

.charting-lite-chart.is-non-interactive {
    pointer-events: none;
}

.charting-lite-chart-legend {
    position: absolute;
    z-index: 4;
    top: 0.85rem;
    left: 0.85rem;
    display: flex;
    max-width: min(72rem, calc(100% - 1.7rem));
    flex-direction: column;
    gap: 0.35rem;
    pointer-events: none;
    border: 1px solid color-mix(in srgb, var(--brand-panel-border) 92%, transparent 8%);
    border-radius: 1rem;
    background: color-mix(in srgb, var(--surface-card) 90%, white 10%);
    padding: 0.55rem 0.75rem;
    backdrop-filter: blur(10px);
}

.charting-lite-chart-legend-primary,
.charting-lite-chart-legend-secondary {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.55rem;
}

.charting-lite-chart-legend-heading {
    display: flex;
    min-width: 0;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0.45rem;
}

.charting-lite-chart-legend-ticker {
    font-size: 0.92rem;
    font-weight: 800;
    line-height: 1.15;
    color: var(--text-color);
}

.charting-lite-chart-legend-name {
    min-width: 0;
    max-width: 32rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.78rem;
    line-height: 1.2;
    color: var(--text-color-secondary);
}

.charting-lite-chart-legend-entry {
    border: 1px solid color-mix(in srgb, var(--brand-panel-border) 88%, transparent 12%);
    border-radius: 999px;
    padding: 0.1rem 0.42rem;
    font-size: 0.64rem;
    font-weight: 800;
    line-height: 1.1;
    color: var(--text-color-secondary);
}

.charting-lite-chart-legend-date {
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--text-color-secondary);
}

.charting-lite-chart-legend-token {
    display: inline-flex;
    align-items: baseline;
    gap: 0.25rem;
    font-size: 0.78rem;
    line-height: 1.2;
}

.charting-lite-chart-legend-token-label {
    font-weight: 700;
    color: var(--text-color-secondary);
}

.charting-lite-chart-legend-token-label--accent {
    color: var(--charting-legend-accent, var(--text-color-secondary));
}

.charting-lite-chart-legend-token-value {
    color: var(--text-color);
}

.charting-lite-chart-tooltip {
    position: absolute;
    z-index: 5;
    display: none;
    pointer-events: none;
    min-width: 11rem;
    border: 1px solid var(--brand-panel-border);
    border-radius: 0.9rem;
    background: color-mix(in srgb, var(--surface-card) 96%, white 4%);
    padding: 0.6rem 0.75rem;
    box-shadow: 0 18px 40px rgba(15, 23, 42, 0.12);
    color: var(--text-color);
    font-size: 0.78rem;
    line-height: 1.45;
    backdrop-filter: blur(12px);
}

.charting-lite-chart-tooltip.is-price-tooltip {
    z-index: 6;
    min-width: 0;
    border-radius: 0.7rem;
    padding: 0.38rem 0.52rem;
    box-shadow: 0 12px 28px rgba(15, 23, 42, 0.14);
    font-size: 0.72rem;
    line-height: 1.2;
}

.charting-lite-chart-tooltip ::v-deep .charting-lite-chart-tooltip__value {
    color: var(--text-color);
    font-weight: 800;
}

.charting-lite-chart-tooltip ::v-deep .charting-lite-chart-tooltip__date {
    margin-top: 0.12rem;
    color: var(--text-color-secondary);
    font-size: 0.66rem;
    font-weight: 700;
    white-space: nowrap;
}

.charting-lite-chart-corporate-actions-layer {
    position: absolute;
    z-index: 4;
    inset-inline: 0;
    bottom: 1.7rem;
    height: 0;
    pointer-events: none;
}

.charting-lite-chart-corporate-action {
    position: absolute;
    z-index: 1;
    bottom: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 0.95rem;
    height: 0.95rem;
    border: 1px solid var(--charting-corporate-action-color);
    border-radius: 9999px;
    background: color-mix(in srgb, var(--surface-card) 92%, white 8%);
    color: var(--charting-corporate-action-color);
    font-size: 0.52rem;
    font-weight: 700;
    line-height: 1;
    pointer-events: auto;
    transform: translateX(-50%);
    padding: 0;
}

@media (max-width: 1279px) {
    .charting-lite-chart:not(.is-compact) {
        min-height: 24rem;
    }
}
</style>
